import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('foodPreferences');
export interface FoodPreferencesRec {
  id: string;
  userId?: string;
  foodPreferences?: string;
  dietaryRestrictions?: string;
  favoriteDishes?: string;
  beveragePreferences?: string;
  specialRequests?: string;
  isLocked?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

class FoodPreferencesService {

  async getAllFoodPreferences(): Promise<FoodPreferencesRec[]> {
    try {
      const response = await http.get('/api/food-preferences');
      return response.data;
    } catch (error) {
      logger.error('Error fetching food preferences:', error);
      throw error;
    }
  }

  async getFoodPreferencesById(id: string): Promise<FoodPreferencesRec> {
    try {
      const response = await http.get(`/api/food-preferences/${id}`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching food preferences by ID:', error);
      throw error;
    }
  }

  async createFoodPreferences(data: Partial<FoodPreferencesRec>): Promise<FoodPreferencesRec> {
    try {
      const response = await http.post('/api/food-preferences', data);
      return response.data;
    } catch (error) {
      logger.error('Error creating food preferences:', error);
      throw error;
    }
  }

  async updateFoodPreferences(id: string, data: Partial<FoodPreferencesRec>): Promise<FoodPreferencesRec> {
    try {
      const response = await http.put(`/api/food-preferences/${id}`, data);
      return response.data;
    } catch (error) {
      logger.error('Error updating food preferences:', error);
      throw error;
    }
  }

  async deleteFoodPreferences(id: string): Promise<void> {
    try {
      await http.delete(`/api/food-preferences/${id}`);
    } catch (error) {
      logger.error('Error deleting food preferences:', error);
      throw error;
    }
  }

  async lockFoodPreferences(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for food preferences ID:', id);
    try {
      const response = await http.post(`/api/food-preferences/${id}/lock`);
      logger.debug('[Service Debug] Lock API response:', response.data);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to lock food preferences');
      }
    } catch (error) {
      logger.error('Error locking food preferences:', error);
      throw error;
    }
  }

  async unlockFoodPreferences(id: string, granteeEmails: string[]): Promise<void> {
    logger.debug('[Service Debug] Calling unlock API for food preferences ID:', id, 'granteeEmails:', granteeEmails);
    try {
      const response = await http.post(`/api/food-preferences/${id}/unlock`, { granteeEmails });
      logger.debug('[Service Debug] Unlock API response:', response.data);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to unlock food preferences');
      }
    } catch (error) {
      logger.error('Error unlocking food preferences:', error);
      throw error;
    }
  }

  async getFoodPreferencesAccessGrants(id: string): Promise<any[]> {
    try {
      const response = await http.get(`/api/food-preferences/${id}/access-grants`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch food preferences access grants');
      }

      return response.data.data || [];
    } catch (error) {
      logger.error('Error fetching food preferences access grants:', error);
      return [];
    }
  }
}

export const foodPreferencesService = new FoodPreferencesService();